<template>
  <v-card elevation="0" flat>
    <BulkImportLoadingDialog
      v-model="showLoadingDialog"
      :loading="importing"
      :title="$t('memberDataSection.parchmentDelivery.bulkImport.importingTitle')"
      :message="$t('memberDataSection.parchmentDelivery.bulkImport.importingNote')"
    />

    <v-card-text style="padding: 0 !important" class="mt-3 text-body-1 black--text">
      {{ $t('memberDataSection.parchmentDelivery.bulkImport.templateNote') }}
    </v-card-text>

    <label for="parchment-deliveries-template-select" class="mb-1 d-block text-body-2 black--text">
      {{ $t('memberDataSection.parchmentDelivery.bulkImport.selectTemplate') }}
    </label>
    <v-select
      id="parchment-deliveries-template-select"
      v-model="selectedTemplate"
      :items="templateOptions"
      item-text="text"
      item-value="value"
      outlined
      dense
      hide-details
      style="max-width: 360px"
      class="mb-1"
    ></v-select>

    <v-btn
      download
      :href="templateDownloadUrl"
      color="primary"
      class="my-3"
      large
      :disabled="importing"
    >
      {{ selectedTemplate === "csv" ? $t("oma.downloadCSVFormat") : $t("oma.downloadXlsFormat") }}
      <v-icon small class="ml-2">mdi-tray-arrow-down</v-icon>
    </v-btn>

    <div class="dropzone" :class="{ 'dropzone--error': showFailedState }">
      <!-- Empty / pick file -->
      <label
        v-if="!file && !showFailedState"
        class="d-flex flex-column align-center justify-center px-3 py-3"
        :class="{ active: dragging }"
        @dragenter="dragging = true"
        @dragleave="dragging = false"
        @drop.prevent="handleFileChangeEvent"
        @dragover.prevent=""
        @click.stop
      >
        <img width="50" src="/img/xlsx-upload.svg" alt="" />
        <input
          ref="fileInput"
          class="d-none"
          type="file"
          accept=".xlsx,.xls,.csv"
          @change="handleFileChangeEvent"
        />
        <span class="font-weight-medium pt-2 text-decoration-underline blue--text darken-4">
          {{ $t('farm.selectFileToUpload') }}
        </span>
        <span>{{ $t('farm.dropHere') }}</span>
      </label>

      <!-- Failed: format or parse error -->
      <div v-else-if="showFailedState" class="dropzone-failed px-4 py-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="failed-label red--text font-weight-bold text-body-1">{{ $t('failed') }}</span>
          <v-btn icon small @click="clearFailedAndFile">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="d-flex align-center mb-2">
          <v-progress-linear
            rounded
            height="8"
            :value="failedProgress"
            color="error"
            background-color="#fde8e8"
          ></v-progress-linear>
          <span class="error--text font-weight-bold ml-2">{{ failedProgress }}%</span>
        </div>
        <p class="text-body-2 black--text mb-4 text-truncate">{{ failedFileName }}</p>
        <div class="dropzone-failed-message px-4 py-5">
          <span
            class="dropzone-failed-message-text error--text text-body-1 font-weight-medium"
            :title="importErrorMessageFull || importErrorMessage"
          >
            {{ importErrorMessage }}
          </span>
        </div>
      </div>

      <!-- File selected, ready to import -->
      <div
        v-else
        class="d-flex flex-column justify-center align-center px-3 py-3"
        style="position: relative; width: 100%; min-height: 100%;"
      >
        <img width="50" src="/img/xlsx-upload.svg" alt="" />
        <div class="mt-2 text-center">
          <span class="text-body-1">{{ file.name }}</span>
        </div>
        <v-btn
          icon
          text
          class="ma-2"
          style="position: absolute; top: 0; right: 0;"
          @click="removeFile"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <p class="pt-3 text-body-2">
      <span class="red--text">{{ $t('memberDataSection.parchmentDelivery.bulkImport.importantNote') }}</span>:
      {{ $t('memberDataSection.parchmentDelivery.bulkImport.importantNoteDetail') }}
    </p>

    <v-divider class="my-5 px-0"></v-divider>

    <v-card-actions class="px-0">
      <v-spacer></v-spacer>
      <v-btn class="mr-2" outlined color="primary" width="112" :disabled="importing" @click="cancelImport">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn
        :disabled="!canImportFile"
        width="112"
        color="primary"
        class="btn-11 btn-22 white--text"
        @click="runImport"
      >
        {{ $t('oma.import') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { parseParchmentDeliveryBulkRows, buildBulkImportResult } from "@/utils/parchmentDeliveryBulkImport";
import ParchmentDeliveryService from "@/_services/ParchmentDeliveryService";
import BulkImportLoadingDialog from "./BulkImportLoadingDialog.vue";

export default {
  name: "ParchmentDeliveryBulkImportTab",
  components: { BulkImportLoadingDialog },
  data() {
    return {
      dragging: false,
      file: null,
      importing: false,
      showFailedState: false,
      importErrorMessage: "",
      importErrorMessageFull: "",
      failedFileName: "",
      failedProgress: 80,
      selectedTemplate: "xls",
      templateOptions: [
        {
          value: "xls",
          text: this.$t("memberDataSection.parchmentDelivery.bulkImport.templateXls"),
        },
        {
          value: "csv",
          text: this.$t("memberDataSection.parchmentDelivery.bulkImport.templateCsv"),
        },
      ],
      acceptedFileExtensions: [".xls", ".xlsx", ".csv"],
    };
  },
  computed: {
    showLoadingDialog: {
      get() {
        return this.importing;
      },
      set(val) {
        if (!val && !this.importing) {
          this.importing = false;
        }
      },
    },
    templateDownloadUrl() {
      if (this.selectedTemplate === "csv") {
        return `${process.env.BASE_URL || "/"}files/parchment-deliveries/parchment-deliveries-bulk-import-template.csv`;
      }

      return `${process.env.BASE_URL || "/"}files/parchment-deliveries/parchment-deliveries-bulk-import-template.xlsx`;
    },
    canImportFile() {
      return Boolean(this.file) && !this.showFailedState && !this.importing;
    },
  },
  methods: {
    defaultFormatErrorMessage() {
      return this.$t("memberDataSection.parchmentDelivery.bulkImport.unsupportedFileFormat");
    },
    showImportError(message, fileName, progress = 80, fullMessage = "") {
      this.showFailedState = true;
      this.importErrorMessage = message || this.defaultFormatErrorMessage();
      this.importErrorMessageFull = fullMessage || this.importErrorMessage;
      this.failedFileName = fileName || "";
      this.failedProgress = progress;
      this.importing = false;
    },
    clearFailedState() {
      this.showFailedState = false;
      this.importErrorMessage = "";
      this.importErrorMessageFull = "";
      this.failedFileName = "";
      this.failedProgress = 80;
    },
    clearFailedAndFile() {
      this.clearFailedState();
      this.removeFile();
    },
    isValidExtension(file) {
      const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
      return this.acceptedFileExtensions.includes(ext);
    },
    handleFileChangeEvent(event) {
      this.dragging = false;
      let files;
      if (event instanceof DragEvent) {
        files = event.dataTransfer.files;
      } else {
        files = event.target?.files;
      }
      if (!(files instanceof FileList) || !files.length) return;

      const picked = files[0];
      this.clearFailedState();

      if (!this.isValidExtension(picked)) {
        this.file = picked;
        this.showImportError(this.defaultFormatErrorMessage(), picked.name, 80);
        return;
      }

      this.file = picked;
    },
    removeFile() {
      this.file = null;
      this.clearFailedState();
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    cancelImport() {
      this.removeFile();
      this.$emit("cancelImport");
    },
    async runImport() {
      if (!this.file || this.showFailedState) return;

      if (!this.isValidExtension(this.file)) {
        this.showImportError(this.defaultFormatErrorMessage(), this.file.name, 80);
        return;
      }

      this.importing = true;

      try {
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Step 1: Parse file into raw rows
        const parsed = await parseParchmentDeliveryBulkRows(this.file);
        if (!parsed.ok) {
          this.showImportError(
            parsed.message || this.$t("memberDataSection.parchmentDelivery.bulkImport.templateErrorIntro"),
            this.file?.name,
            80,
            parsed.messageFull || parsed.message
          );
          return;
        }

        // Step 2: Fetch matching DB records by outturn number
        const outturnNums = parsed.rows.map((r) => r.outturnNumber).filter(Boolean);
        let dbRecords = [];
        try {
          const res = await ParchmentDeliveryService.lookupByOutturnNumbers(outturnNums);
          dbRecords = res?.data?.records || [];
        } catch (_) {
          // Proceed without DB records — all rows will be flagged as OUTTURN_NUMBER_MISMATCH
        }

        // Step 3: Classify and emit (include original file for S3 upload on save)
        const resultData = buildBulkImportResult(parsed.rows, dbRecords);
        this.$emit("importSuccess", { ...resultData, _uploadedFile: this.file });
      } catch (err) {
        console.error(err);
        const msg =
          err?.message ||
          this.$t("memberDataSection.parchmentDelivery.bulkImport.invalidFileFormat");
        this.showImportError(msg, this.file?.name, 80);
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dropzone {
  height: 280px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  border: 2px dashed $gray;
  border-radius: 8px;
  background: #fff;
}
.dropzone--error {
  border-color: #e53935;
  border-width: 2px;
}
.dropzone label {
  cursor: pointer;
  width: 100%;
  height: 100%;
  min-height: 260px;
}
.dropzone label:hover,
.dropzone label.active {
  background-color: #f5f5f5;
}
.dropzone-failed {
  width: 100%;
  min-height: 260px;
}
.dropzone-failed-message {
  border: 1px solid #e57373;
  background-color: #ffebee;
  border-radius: 4px;
}
.dropzone-failed-message-text {
  display: inline-block;
  width: 100%;
  text-align: left;
}
.failed-label {
  font-size: 14px;
}
</style>

